import { SentSuggestionSchema } from '@/schemas/api/ai/suggest-price';
/**
 * Admin Edit Listing API Endpoint Schemas
 *
 * POST /api/listings/[id]/admin-edit
 * Allows admins to update listing title, text, price, categories, and tags
 */

import { z } from 'zod';
import { MAX_LISTING_PHOTOS } from '../../../data/js/listing-photos';

// Request schema
export const AdminEditRequestSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  text: z.string().max(5000).optional(),
  price: z.string().optional(),
  categoryId: z.number().int().positive().nullable().optional(),
  categoryGroupId: z.number().int().positive().nullable().optional(),
  tags: z.array(z.string().max(100)).max(20).optional(),
  photo: z.object({
    // Shares the per-listing limit; an 8 here would have rejected any edit to a
    // listing that legitimately carries more than 8 photos.
    totalCount: z.number().int().min(0).max(MAX_LISTING_PHOTOS),
  }).optional(),
  /** The panel's AI price suggestion, kept when the listing is saved at it (utils/price-suggestion-signal.ts). */
  priceSuggestion: SentSuggestionSchema.optional(),
});

export type AdminEditRequest = z.infer<typeof AdminEditRequestSchema>;

// Listing in response
export const AdminEditListingSchema = z.object({
  id: z.number(),
  title: z.string(),
  text: z.string().nullable(),
  price: z.string(),
  categoryId: z.number().nullable(),
  categoryGroupId: z.number().nullable(),
  tags: z.array(z.string()),
});

export type AdminEditListing = z.infer<typeof AdminEditListingSchema>;

// Presigned URL schema
export const PresignedUrlSchema = z.object({
  photoIndex: z.number(),
  size: z.string(),
  presignedUrl: z.string(),
});

// Success response
export const AdminEditSuccessSchema = z.object({
  success: z.literal(true),
  listing: AdminEditListingSchema,
  presignedUrls: z.array(PresignedUrlSchema).optional(),
});

export type AdminEditSuccess = z.infer<typeof AdminEditSuccessSchema>;

// Error response
export const AdminEditErrorSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

export type AdminEditError = z.infer<typeof AdminEditErrorSchema>;

// Combined response schema
export const AdminEditResponseSchema = z.discriminatedUnion('success', [
  AdminEditSuccessSchema,
  AdminEditErrorSchema,
]);

export type AdminEditResponse = z.infer<typeof AdminEditResponseSchema>;
